import parse, { domToReact } from 'html-react-parser';
import dynamic from 'next/dynamic';

import { toInternalLink, toValidInternalLink } from 'lib/links';
import { isUrlValidForInternalUsage } from 'lib/string';

import {
  doesAnyDomNodeParentContainName,
  isLineBreaker,
  isYoutubeIframe,
  strip,
} from './htmlParserUtils';

import HTML_PARSER from 'constants/htmlParser';

const Text = dynamic(() => import('shopper/components/Text'));
const NextLink = dynamic(() => import('next/link'));
const Anchor = dynamic(() => import('components/Anchor'));
const YoutubeFacade = dynamic(() => import('components/YoutubeFacade'));
const Image = dynamic(() => import('components/Image'));
const ListGroup = dynamic(() => import('components/List/ListGroup'));
const ListItem = dynamic(() => import('components/List/ListItem'));

const offerAdditionalInfosTextOptions = {
  trim: true,
  replace: (domNode) => {
    const { name, attribs, children, type, data } = domNode;

    /**
     * We ignores unmapped nodes to prevent possible conflicts between inline
     * styles with Emotion. And some texts aren't wrapped to a tag. So, because
     * this, we needs explicit to return the text
     */
    if (type === 'text' && data && !isLineBreaker(data)) {
      if (
        doesAnyDomNodeParentContainName(
          domNode?.parent,
          HTML_PARSER.NAME_PARAGRAPH
        )
      ) {
        return <span>{strip(data)}</span>;
      }

      if (domNode?.parent === null) {
        return <Text as="span">{strip(data)}</Text>;
      }

      return data.trim();
    }

    if (name === HTML_PARSER.NAME_BOLD) {
      return (
        <Text as="span" bold>
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_PARAGRAPH) {
      return (
        <Text as="span">
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_EM) {
      return (
        <Text as="em" italic>
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_LIST_ITEM) {
      return (
        <ListItem>
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </ListItem>
      );
    }

    if (
      [HTML_PARSER.NAME_UNORDERED_LIST, HTML_PARSER.NAME_ORDERED_LIST].includes(
        name
      )
    ) {
      const isOrderedList = children[0].name === HTML_PARSER.NAME_ORDERED_LIST;

      return (
        <ListGroup
          type={isOrderedList ? 'ordered' : 'unordered'}
          spacing={2}
          mb={1}
        >
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </ListGroup>
      );
    }

    if (name === HTML_PARSER.NAME_BR) {
      return <br />;
    }

    if (name === HTML_PARSER.NAME_HREF) {
      const { href } = attribs;

      if (isUrlValidForInternalUsage(href)) {
        return (
          <NextLink
            href={toValidInternalLink(toInternalLink(href))}
            prefetch={false}
            passHref
          >
            <Anchor>{domNode.lastChild?.data || children[0]?.data}</Anchor>
          </NextLink>
        );
      }

      return (
        <Anchor href={href} rel="noopener" target="_blank">
          {domNode.lastChild?.data || children[0]?.data}
        </Anchor>
      );
    }

    if (name === HTML_PARSER.NAME_IMAGE) {
      const { alt, src } = attribs ?? {};

      return (
        <Image
          maxWidth="100%"
          mx="auto"
          sizes="(max-width: 420px) 400px, (max-width: 768px) 750px, 880px"
          alt={alt}
          my={4}
          src={src}
        />
      );
    }

    if (name === HTML_PARSER.NAME_IFRAME && isYoutubeIframe(attribs.src)) {
      return (
        <YoutubeFacade
          src={attribs.src}
          title={attribs.title}
          width="100%"
          marginBottom={8}
        />
      );
    }

    return null;
  },
};

const toOfferAdditionalInfosText = (offerAdditionalInfoText) =>
  parse(offerAdditionalInfoText, offerAdditionalInfosTextOptions);

export default toOfferAdditionalInfosText;
